import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dumbbell, Lock, Loader2, ClipboardList, TrendingUp, Utensils, ShieldCheck, Compass, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Evaluation } from "../components/fitness/Evaluation";
import { PlanView } from "../components/fitness/PlanView";
import { ProgressView } from "../components/fitness/ProgressView";
import { isoWeek, fmtWeight, fmtDelta } from "../components/fitness/units";

const MSGS = ["Reading your evaluation…", "Balancing training days and rest…", "Setting safe calorie and protein guides…", "Writing your weekly structure…", "Picking milestones you can actually hit…", "Almost there — double-checking safety rules…"];

function Generating() {
  const [i, setI] = useState(0);
  const [pct, setPct] = useState(4);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % MSGS.length), 4000);
    const p = setInterval(() => setPct((x) => Math.min(94, x + (x < 60 ? 2 : 0.6))), 700);
    return () => { clearInterval(t); clearInterval(p); };
  }, []);
  return (
    <div className="bg-slate-950 text-white rounded-2xl p-10 text-center relative overflow-hidden" data-testid="fitness-generating">
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,.25),transparent_60%)]" />
      <div className="relative">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center"><Loader2 className="w-7 h-7 text-amber-400 animate-spin" /></div>
        <p className="font-heading text-3xl mt-5">Building your plan</p>
        <p className="text-sm text-slate-300 mt-1 h-5" data-testid="fitness-generating-msg">{MSGS[i]}</p>
        <div className="mt-6 max-w-md mx-auto h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-amber-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} /></div>
        <p className="text-xs text-slate-500 mt-3">Usually about a minute — the coach is reading your numbers, not a template.</p>
      </div>
    </div>
  );
}

function StatusStrip({ state, units }) {
  const s = state.summary, f = state.food?.totals || {}, n = state.plan.nutrition;
  const items = [
    ["This week", `${s.workouts_this_week}/${s.workouts_planned}`, "workouts done"],
    ["Protein today", `${f.protein_g || 0}`, `of ${n.protein_g[0]}–${n.protein_g[1]} g`],
    ["Calories today", `${f.calories || 0}`, `of ${n.daily_calories[0]}–${n.daily_calories[1]}`],
    ["Weight", fmtWeight(s.weight_now_kg, units), `${fmtDelta(s.weight_change_kg, units)} since start`],
    ["Consistency", `${s.adherence_pct}%`, "last 4 weeks"],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-white/10 border-t border-white/10 mt-6 -mx-6 sm:-mx-8 -mb-6 sm:-mb-8" data-testid="fitness-status-strip">
      {items.map(([l, v, sub]) => <div key={l} className="px-5 py-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{l}</p><p className="font-heading text-xl font-bold text-white leading-tight">{v}</p><p className="text-[11px] text-slate-500">{sub}</p></div>)}
    </div>
  );
}

const UNLOCKS = [
  [Dumbbell, "Two workout options every day", "Strength or a cardio circuit — tap one and a full session is built for your equipment and limits."],
  [Utensils, "Food & macro tracking", "Type what you ate; calories, protein, carbs and fat are estimated and checked against your targets."],
  [TrendingUp, "Progress tracking", "Weight, lifts, cardio, check-offs and charts — and a plan that updates with your progress."],
  [ShieldCheck, "Your personal guide", "Where you are, where you're going, honest pros & cons, milestones and safety built in."],
];

function Unlocks({ profileSaved, onBuild }) {
  return (
    <div className="bg-slate-950 text-white rounded-2xl p-6 sm:p-8 mb-6" data-testid="fitness-unlocks">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Step 1 of 1 · unlock your plan</p>
          <h2 className="font-heading text-2xl sm:text-3xl mt-2">Everything below opens up the moment your evaluation is done.</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">Nothing here is generic — the coach needs your numbers first so every workout, target and tip is built for you. Two minutes, then it's all yours.</p>
        </div>
        {profileSaved && <Button onClick={onBuild} className="bg-amber-400 text-slate-950 hover:bg-amber-300" data-testid="fitness-generate-button">Build my plan now</Button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {UNLOCKS.map(([Icon, t, d]) => (
          <div key={t} className="rounded-xl border border-white/10 bg-white/5 p-4"><Icon className="w-5 h-5 text-amber-400" /><p className="font-semibold text-sm mt-2">{t}</p><p className="text-xs text-slate-400 mt-1 leading-snug">{d}</p></div>
        ))}
      </div>
    </div>
  );
}

export default function Fitness() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("train");
  const [generating, setGenerating] = useState(false);
  const poll = useRef(null);
  const isPro = !!user?.is_premium;

  const load = useCallback(async () => { const { data } = await api.get("/fitness"); setState(data); return data; }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load your fitness data.")); return () => clearInterval(poll.current); }, [load]);

  const startPolling = useCallback(() => {
    setGenerating(true);
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get("/fitness/plan/status");
        if (data.status === "done") { clearInterval(poll.current); await load(); setGenerating(false); setTab("train"); toast.success("Your plan is ready."); }
        else if (data.status === "error" || data.status === "idle") { clearInterval(poll.current); setGenerating(false); toast.error(data.error || "Couldn't build your plan. Please try again."); }
      } catch { /* keep polling */ }
    }, 3000);
  }, [load]);

  const generate = async () => {
    try { await api.post("/fitness/plan"); startPolling(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onEvaluated = async () => { try { await load(); } catch { /* state refreshes on next load */ } generate(); };
  const onCheck = async (key, done) => {
    setState((s) => ({ ...s, checks: done ? [...s.checks, key] : s.checks.filter((k) => k !== key) }));
    try { const { data } = await api.put("/fitness/check", { key, done }); setState((s) => ({ ...s, checks: data.checks, summary: data.summary })); }
    catch { toast.error("Could not save that."); }
  };

  if (!state) return <div className="min-h-screen bg-slate-50"><Navbar /></div>;
  const units = state.profile?.units || "us";
  const unlocked = !!state.profile && !!state.plan;
  const showEval = !unlocked || tab === "evaluation";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className={`rounded-2xl ${unlocked ? "bg-slate-950 text-white p-6 sm:p-8 relative overflow-hidden" : ""}`} data-testid="fitness-header">
          {unlocked && <div className="absolute inset-0 pointer-events-none opacity-60 bg-[radial-gradient(ellipse_at_top_right,rgba(220,38,38,.25),transparent_55%)]" />}
          <div className="relative">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3"><Dumbbell className={`w-4 h-4 ${unlocked ? "text-amber-400" : "text-primary"}`} /><p className={`text-xs font-bold uppercase tracking-[0.2em] ${unlocked ? "text-amber-400" : "text-primary"}`}>Fitness Plan <span className="ml-1 rounded-full border border-current px-1.5 py-px text-[9px] tracking-wider" data-testid="fitness-beta-badge">Beta</span>{unlocked && ` · ${new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}`}</p></div>
                <h1 className={`font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2 ${unlocked ? "text-white" : "text-slate-900"}`} data-testid="fitness-title">Train like the job demands.</h1>
                {!state.profile && <p className="mt-3 text-slate-600 max-w-2xl">Where you are, where you're going, and exactly what to do next — with a plan that adapts as you log progress.</p>}
              </div>
              {unlocked && (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur" data-testid="fitness-tabs">
                    {[["train", "Train", Dumbbell], ["food", "Food", Utensils], ["progress", "Progress", TrendingUp], ["guide", "Guide", Compass]].map(([id, l, Icon]) => (
                      <button key={id} type="button" onClick={() => setTab(id)} className={`inline-flex items-center gap-2 text-sm font-semibold px-3 sm:px-4 py-2 rounded-full transition-colors ${tab === id ? "bg-amber-400 text-slate-950" : "text-white hover:bg-white/10"}`} data-testid={`fitness-tab-${id}`}><Icon className="w-4 h-4" /> {l}</button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setTab(tab === "evaluation" ? "train" : "evaluation")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-amber-300 transition-colors" data-testid="fitness-tab-evaluation">
                    {tab === "evaluation" ? <><ArrowLeft className="w-3.5 h-3.5" /> Back to my plan</> : <><ClipboardList className="w-3.5 h-3.5" /> Edit evaluation</>}
                  </button>
                </div>
              )}
            </div>
            {unlocked && <StatusStrip state={state} units={units} />}
          </div>
        </div>

        <div className="mt-6">
          {generating ? <Generating /> : showEval ? (
            <>
              {!unlocked && <Unlocks profileSaved={!!state.profile} onBuild={generate} />}
              <Evaluation key="real" profile={state.profile} onSaved={onEvaluated} />
            </>
          ) : tab === "progress" ? (
            <ProgressView state={state} units={units} onState={setState} onCheck={onCheck} />
          ) : state.plan ? (
            <PlanView isPro={isPro} tab={tab} plan={state.plan} checks={state.checks} weekKey={isoWeek()} onCheck={onCheck} units={units} onRegenerate={generate} regenerating={generating} summary={state.summary} workouts={state.workouts} rails={state.rails} food={state.food} meals={state.meals} onState={setState} />
          ) : null}
        </div>
        <p className="mt-8 text-xs text-slate-400" data-testid="fitness-disclaimer">{state.disclaimer}</p>
      </div>
    </div>
  );
}
